import {
  createContext,
  useContext,
  useEffect,
  useMemo,
  useRef,
  useState,
  type ReactNode,
} from 'react';
import { useQueryClient } from '@tanstack/react-query';
import {
  annotationsOf,
  commitWorkingSnapshot,
  createCommit,
  draftMovePath,
  draftRemovePath,
  draftRenamePath,
  draftSetAnnotation,
  draftUpsertFile,
  draftUpsertFiles,
  ensureAgentsGuide,
  filesWithin,
  hasAgentsGuide,
  isHiddenMetaPath,
  keepPathForFolder,
  migrateLegacyAnnotations,
  withAgentsGuide,
  withAnnotationsFile,
  type EntryAnnotation,
  type EntryAnnotations,
  type RepositoryFile,
  type RepositorySnapshot,
} from '@trove/core';
import {
  createRecoveryMnemonic,
  encodeRootKeyEnvelope,
  fromBase64,
  generateDeviceKeys,
  parseRootKeyEnvelope,
  randomBytes,
  serializeWrappedKey,
  sign,
  toBase64,
  unwrapKey,
  unwrapRootKeyFromRecovery,
  wrapKey,
  wrapRootKeyForRecovery,
  type DeviceKeys,
} from '@trove/crypto';
import type {
  EncryptedObject,
  Repository,
  SaveMode,
  ShareRecord,
  VaultMode,
} from '@trove/contracts';
import { toastManager } from '@trove/ui';
import {
  api,
  ApiClientError,
  createRepository as createRepositoryRequest,
  deleteJson,
  getRepositories,
  postJson,
  putJson,
} from './api';
import {
  ensureSnapshotFilesContent,
  loadSnapshotFromHeadObject,
  uploadRepositorySnapshot,
} from './objectStore';
import { commitGitSnapshot, pullOpenVault } from './openVault';
import { loadSaveMode, saveSaveMode } from './preferences';
import { clearPendingPasskeyUnlocks, loadPendingPasskeyUnlocks } from './passkeys';
import {
  deserializeDeviceKeys,
  listWorkspaces,
  loadWorkspace,
  saveWorkspace,
  serializeDeviceKeys,
  setActiveRepositoryId,
  type StoredWorkspace,
} from './storage';
import { useSession } from './session';
import { DEFAULT_OPEN_VAULT_ICON, DEFAULT_VAULT_ICON } from './vaultIcons';

const encoder = new TextEncoder();
const decoder = new TextDecoder();

const uid = (prefix: string) => `${prefix}_${crypto.randomUUID().replaceAll('-', '')}`;

const DEMO_FILES: Array<[string, string, string]> = [
  ['private/notes.md', '# Private notes\n\nRenew passport in September.\n', 'text/markdown'],
  [
    'private/id-scan.svg',
    `<svg xmlns="http://www.w3.org/2000/svg" width="160" height="100" viewBox="0 0 160 100">
  <rect width="160" height="100" rx="8" fill="#e8f2ec"/>
  <rect x="14" y="18" width="52" height="64" rx="6" fill="#9ec9b2"/>
  <circle cx="40" cy="42" r="12" fill="#f7fbf8"/>
  <rect x="28" y="58" width="24" height="12" rx="6" fill="#f7fbf8"/>
  <rect x="78" y="24" width="66" height="8" rx="4" fill="#6f9f88"/>
  <rect x="78" y="42" width="54" height="6" rx="3" fill="#b7d7c6"/>
  <rect x="78" y="56" width="48" height="6" rx="3" fill="#b7d7c6"/>
  <rect x="78" y="70" width="40" height="6" rx="3" fill="#b7d7c6"/>
</svg>`,
    'image/svg+xml',
  ],
  [
    'private/welcome.html',
    `<!doctype html><html><body style="margin:0;font:14px ui-sans-serif,system-ui;background:#f7faf8;color:#173328">
<main style="padding:24px"><h1 style="margin:0 0 8px;font-size:22px">Welcome</h1>
<p style="margin:0;opacity:.75">Locally decrypted HTML preview.</p>
<div style="margin-top:18px;height:48px;border-radius:10px;background:linear-gradient(120deg,#9ec9b2,#d8f3e4)"></div>
</main></body></html>`,
    'text/html',
  ],
  [
    'health/medications.md',
    '# Medications\n\n- Atorvastatin — 10mg nightly\n- Vitamin D — 1000 IU daily\n',
    'text/markdown',
  ],
  [
    'health/allergies.json',
    '{\n  "medications": ["penicillin"],\n  "foods": []\n}\n',
    'application/json',
  ],
  [
    'health/labs/2026-04-bloodwork.pdf',
    '%PDF-1.4\n% Synthetic laboratory report for demonstration only.\n',
    'application/pdf',
  ],
  [
    'health/labs/notes.md',
    '# April labs\n\nAll values within the reference range. Synthetic demo data.\n',
    'text/markdown',
  ],
  [
    'health/therapy/notes.md',
    '# Therapy notes\n\nPrivate reflection. This sibling must never be exposed with labs.\n',
    'text/markdown',
  ],
  [
    'shared/README.md',
    '# Shared with me\n\nFolders other people share with you appear here.\n',
    'text/markdown',
  ],
];

const DEMO_ANNOTATIONS: EntryAnnotations = {
  health: { icon: 'stethoscope', tags: ['health'] },
  'health/medications.md': { icon: 'pill', tags: ['rx'] },
  private: { icon: 'lock', tags: ['personal'] },
};

const makeFiles = (seeded: boolean): RepositoryFile[] => {
  const createdAt = new Date().toISOString();
  const source: Array<[string, string, string]> = seeded
    ? DEMO_FILES
    : [
        ['private/README.md', '# Private\n\nYour private files live here.\n', 'text/markdown'],
        ['shared/README.md', '# Shared\n\nFolders shared with you appear here.\n', 'text/markdown'],
      ];
  const files = source.map(([path, content, mimeType]) => ({
    path,
    content,
    mimeType,
    size: encoder.encode(content).length,
    modifiedAt: createdAt,
  }));
  const withMeta = seeded ? withAnnotationsFile(files, DEMO_ANNOTATIONS, createdAt) : files;
  return withAgentsGuide(withMeta, createdAt);
};

async function readHeadObject(opaqueHeadId: string): Promise<EncryptedObject> {
  const result = await postJson<{ objects: EncryptedObject[] }>('/v1/objects/read', {
    ids: [opaqueHeadId],
  });
  const object = result.objects[0];
  if (!object) throw new Error('The encrypted repository head is unavailable');
  return object;
}

async function publishSnapshotHead(
  snapshot: RepositorySnapshot,
  workspace: Pick<StoredWorkspace, 'collectionId' | 'collectionKey' | 'deviceKeys'>,
  expectedVersion: number,
): Promise<RepositorySnapshot> {
  const keys = deserializeDeviceKeys(workspace.deviceKeys);
  const packed = await uploadRepositorySnapshot(
    snapshot,
    Uint8Array.from(workspace.collectionKey),
    workspace.collectionId,
    keys,
  );
  await putJson(`/v1/collections/${workspace.collectionId}/head`, {
    expectedVersion,
    nextHead: packed.headObjectId,
    signature: toBase64(sign(encoder.encode(packed.headObjectId), keys.signingPrivateKey)),
  });
  return { ...packed.snapshot, version: expectedVersion + 1 };
}

interface WorkspaceContextValue {
  workspace: StoredWorkspace | null;
  vaults: StoredWorkspace[];
  loading: boolean;
  syncing: boolean;
  demoSetupError?: string;
  dirty: boolean;
  saveMode: SaveMode;
  setSaveMode(mode: SaveMode): void;
  createRepository(
    mnemonic: string,
    seeded?: boolean,
    demoInfo?: { instanceId: string; recipientEmail: string },
    options?: { mode?: VaultMode; label?: string; icon?: string; slug?: string },
  ): Promise<void>;
  createOpenVault(options: { label: string; icon?: string; slug?: string }): Promise<void>;
  switchVault(repositoryId: string): Promise<void>;
  restoreRepository(mnemonic: string, repositoryId?: string): Promise<void>;
  unlockFromDevicePairing(
    deviceId: string,
    deviceKeys: DeviceKeys,
    repositoryId?: string,
  ): Promise<void>;
  saveFile(
    path: string,
    content: string,
    mimeType?: string,
    annotation?: EntryAnnotation | null,
  ): Promise<void>;
  saveFiles(files: Array<{ path: string; content: string; mimeType: string }>): Promise<void>;
  createFolder(path: string, annotation?: EntryAnnotation | null): Promise<void>;
  moveEntry(sourcePath: string, targetFolder: string): Promise<void>;
  renameEntry(sourcePath: string, nextName: string): Promise<string>;
  deleteEntry(sourcePath: string): Promise<void>;
  setEntryAnnotation(path: string, annotation: EntryAnnotation | null): Promise<void>;
  updateFolder(
    path: string,
    next: { name?: string; annotation?: EntryAnnotation | null },
  ): Promise<string>;
  commitPending(message?: string): Promise<void>;
  discardPending(): Promise<void>;
  shareFolder(
    folder: string,
    recipientEmail: string,
    options: {
      expiresAt: string | null;
      maxViews?: 1 | null;
      accessCode?: string | null;
    },
  ): Promise<void>;
  acceptIncomingShare(share: ShareRecord, accessCode?: string): Promise<void>;
  revokeShare(shareId: string): Promise<void>;
  refresh(): Promise<void>;
}

const WorkspaceContext = createContext<WorkspaceContextValue | null>(null);

export function WorkspaceProvider({ children }: { children: ReactNode }) {
  const { user } = useSession();
  const queryClient = useQueryClient();
  const [workspace, setWorkspace] = useState<StoredWorkspace | null>(null);
  const [vaults, setVaults] = useState<StoredWorkspace[]>([]);
  const [loading, setLoading] = useState(true);
  const [syncing, setSyncing] = useState(false);
  const [demoSetupError, setDemoSetupError] = useState<string>();
  const [saveMode, setSaveModeState] = useState<SaveMode>(() => loadSaveMode());
  const seedingUser = useRef<string | undefined>(undefined);

  const dirty = Boolean(workspace?.pendingBaseline);
  const author = user?.displayName ?? 'Trove user';

  const setSaveMode = (mode: SaveMode) => {
    saveSaveMode(mode);
    setSaveModeState(mode);
  };

  async function installLockedRepository(
    repository: Repository,
    collectionKey: Uint8Array,
  ): Promise<StoredWorkspace> {
    if (!user) throw new Error('Sign in required');
    const object = await readHeadObject(repository.head);
    if (object.kind !== 'manifest') {
      throw new Error(
        'Repository head is not a manifest yet. Open the vault in the browser on a device that still has a local copy so it can republish.',
      );
    }
    const remoteSnapshot = await loadSnapshotFromHeadObject(object, collectionKey, {
      hydrateHead: true,
    });
    const needsAgents = !hasAgentsGuide(remoteSnapshot.commits.at(-1)?.files ?? []);
    let snapshot = ensureAgentsGuide(remoteSnapshot);
    const deviceKeys = generateDeviceKeys();
    const deviceId = uid('device');
    const deviceWrap = await wrapKey(collectionKey, deviceKeys.encryptionPublicKey);
    await postJson('/v1/devices', {
      deviceId,
      label: 'This browser',
      encryptionPublicKey: toBase64(deviceKeys.encryptionPublicKey),
      signingPublicKey: toBase64(deviceKeys.signingPublicKey),
      deviceWrap,
      repositoryId: repository.id,
    });
    const nextWorkspace: StoredWorkspace = {
      userId: user.id,
      repositoryId: repository.id,
      collectionId: repository.rootCollectionId,
      snapshot,
      collectionKey: [...collectionKey],
      deviceKeys: serializeDeviceKeys(deviceKeys),
      shareCollections: {},
      mode: 'owner',
      vaultMode: 'locked',
      label: repository.label || 'Personal',
      icon: repository.icon,
      slug: repository.slug,
      deviceId,
    };
    if (needsAgents) {
      snapshot = await commitWorkingSnapshot(snapshot, 'Add AGENTS.md', user.displayName);
      await syncCommitted(nextWorkspace, snapshot);
      return (await loadWorkspace(user.id, repository.id)) ?? nextWorkspace;
    }
    await saveWorkspace(nextWorkspace);
    setWorkspace(nextWorkspace);
    return nextWorkspace;
  }

  const refresh = async () => {
    if (!user) {
      setWorkspace(null);
      setVaults([]);
      setLoading(false);
      return;
    }
    setLoading(true);
    let stored = await loadWorkspace(user.id);
    const pendingUnlocks = loadPendingPasskeyUnlocks();
    if (stored && pendingUnlocks.length > 0) {
      clearPendingPasskeyUnlocks();
    } else if (!stored) {
      if (pendingUnlocks.length > 0) {
        clearPendingPasskeyUnlocks();
        try {
          const { repositories } = await getRepositories();
          for (const pending of pendingUnlocks) {
            const repository = repositories.find(
              (candidate) => candidate.id === pending.repositoryId && candidate.mode === 'locked',
            );
            if (repository) {
              await installLockedRepository(repository, Uint8Array.from(pending.collectionKey));
            }
          }
          stored = await loadWorkspace(user.id);
          if (stored) {
            toastManager.add({
              title: 'Unlocked with passkey',
              description: 'This browser can now open your encrypted files.',
            });
          }
        } catch (cause) {
          toastManager.add({
            title: 'Passkey signed in, but could not unlock the vault',
            description: cause instanceof Error ? cause.message : 'Use your backup phrase instead.',
          });
        }
      }
    }
    let shouldSeedAgents = false;
    if (stored) {
      const hadLegacy =
        'annotations' in stored.snapshot ||
        (stored.pendingBaseline !== undefined && 'annotations' in stored.pendingBaseline);
      const headMissingAgents = !hasAgentsGuide(stored.snapshot.commits.at(-1)?.files ?? []);
      const snapshot = ensureAgentsGuide(
        migrateLegacyAnnotations(
          stored.snapshot as RepositorySnapshot & { annotations?: EntryAnnotations },
        ),
      );
      const pendingBaseline = stored.pendingBaseline
        ? ensureAgentsGuide(
            migrateLegacyAnnotations(
              stored.pendingBaseline as RepositorySnapshot & { annotations?: EntryAnnotations },
            ),
          )
        : undefined;
      shouldSeedAgents =
        stored.mode === 'owner' && (stored.vaultMode ?? 'locked') === 'locked' && headMissingAgents;
      stored = {
        ...stored,
        snapshot,
        pendingBaseline,
        vaultMode: stored.vaultMode ?? 'locked',
        label: stored.label || 'Personal',
      };
      if (hadLegacy || shouldSeedAgents) await saveWorkspace(stored);
    }
    // A locally unlocked vault is ready to use. Refresh its remote head in the background
    // rather than holding the initial render behind network and decryption work.
    if (stored) {
      setWorkspace(stored);
      setLoading(false);
    }
    if (
      stored?.mode === 'owner' &&
      (stored.vaultMode ?? 'locked') === 'locked' &&
      !stored.pendingBaseline
    ) {
      try {
        const remote = await api<{
          collectionId: string;
          opaqueHeadId: string;
          version: number;
        }>(`/v1/collections/${stored.collectionId}/head`);
        const headObject = await readHeadObject(remote.opaqueHeadId);
        const localUnpacked = stored.snapshot.files.some((file) => !file.blobOpaqueId);

        if (localUnpacked) {
          const published = await publishSnapshotHead(stored.snapshot, stored, remote.version);
          shouldSeedAgents = !hasAgentsGuide(published.commits.at(-1)?.files ?? []);
          stored = { ...stored, snapshot: published };
          await saveWorkspace(stored);
        } else if (headObject.kind === 'manifest' && remote.version > stored.snapshot.version) {
          const remoteSnapshot = ensureAgentsGuide(
            await loadSnapshotFromHeadObject(headObject, Uint8Array.from(stored.collectionKey), {
              hydrateHead: true,
            }),
          );
          shouldSeedAgents = !hasAgentsGuide(remoteSnapshot.commits.at(-1)?.files ?? []);
          stored = {
            ...stored,
            snapshot: { ...remoteSnapshot, version: remote.version },
          };
          await saveWorkspace(stored);
        } else if (headObject.kind !== 'manifest') {
          throw new Error(
            'Remote head is not a manifest. Open this vault on a device with a local copy to republish.',
          );
        }
      } catch (cause) {
        toastManager.add({
          title: 'Could not pull the latest repository',
          description: cause instanceof Error ? cause.message : 'Using this device’s local copy.',
        });
      }
    }
    if (
      stored &&
      shouldSeedAgents &&
      stored.mode === 'owner' &&
      (stored.vaultMode ?? 'locked') === 'locked' &&
      !stored.pendingBaseline
    ) {
      try {
        const committed = await commitWorkingSnapshot(
          stored.snapshot,
          'Add AGENTS.md',
          user.displayName,
        );
        await syncCommitted(stored, committed);
        stored = { ...stored, snapshot: committed, pendingBaseline: undefined };
      } catch (cause) {
        toastManager.add({
          title: 'Could not sync AGENTS.md',
          description: cause instanceof Error ? cause.message : 'It will retry on the next save.',
        });
      }
    }
    setWorkspace(stored);
    setVaults(await listWorkspaces(user.id));
    setLoading(false);
  };

  useEffect(() => {
    setDemoSetupError(undefined);
    void refresh();
  }, [user?.id]);

  useEffect(() => {
    if (!user?.demo || loading || workspace || user.displayName !== 'William') return;
    if (seedingUser.current === user.id) return;
    seedingUser.current = user.id;
    void (async () => {
      setDemoSetupError(undefined);
      const existing = await loadWorkspace(user.id);
      if (existing) {
        setWorkspace(existing);
        return;
      }
      const demoInfo = sessionStorage.getItem('trove-demo-instance');
      const parsed = demoInfo
        ? (JSON.parse(demoInfo) as { instanceId: string; recipientEmail: string })
        : undefined;
      await createRepository(createRecoveryMnemonic(), true, parsed);
    })().catch((cause) => {
      const description = cause instanceof Error ? cause.message : 'Please restart the demo.';
      setDemoSetupError(description);
      toastManager.add({
        title: 'Demo setup failed',
        description,
      });
    });
  }, [user?.id, user?.demo, user?.displayName, loading, workspace]);

  async function createRepository(
    mnemonic: string,
    seeded = false,
    demoInfo?: { instanceId: string; recipientEmail: string },
    options?: { mode?: VaultMode; label?: string; icon?: string; slug?: string },
  ) {
    if (!user) throw new Error('Sign in required');
    const vaultMode = options?.mode ?? 'locked';
    const label = options?.label?.trim() || (vaultMode === 'open' ? 'Shared' : 'Personal');
    const icon =
      options?.icon || (vaultMode === 'open' ? DEFAULT_OPEN_VAULT_ICON : DEFAULT_VAULT_ICON);
    if (vaultMode === 'open') {
      await createOpenVault({ label, icon, slug: options?.slug });
      return;
    }
    setSyncing(true);
    try {
      const deviceKeys = workspace?.deviceKeys
        ? deserializeDeviceKeys(workspace.deviceKeys)
        : generateDeviceKeys();
      const collectionKey = randomBytes(32);
      const repositoryId = uid('repo');
      const collectionId = uid('collection');
      const deviceId = uid('device');
      const files = makeFiles(seeded);
      const firstCommit = await createCommit(
        files,
        null,
        seeded ? 'Seed synthetic demo repository' : `Create ${label} vault`,
        user.displayName,
      );
      const snapshot: RepositorySnapshot = {
        repositoryId,
        collectionId,
        files,
        commits: [firstCommit],
        head: firstCommit.id,
        version: 0,
      };
      const wrappedForDevice = await wrapKey(collectionKey, deviceKeys.encryptionPublicKey);
      const recovery = await wrapRootKeyForRecovery(collectionKey, mnemonic);
      // Register the collection before its encrypted objects are uploaded. The object endpoint
      // deliberately rejects writes to unknown collections.
      await createRepositoryRequest({
        repositoryId,
        rootCollectionId: collectionId,
        deviceId,
        deviceEncryptionPublicKey: toBase64(deviceKeys.encryptionPublicKey),
        deviceSigningPublicKey: toBase64(deviceKeys.signingPublicKey),
        wrappedRootKey: encodeRootKeyEnvelope({
          devices: { [deviceId]: wrappedForDevice },
          recovery,
        }),
        head: '0'.repeat(40),
        mode: 'locked',
        label,
        icon,
        slug: options?.slug,
      });
      const packed = await uploadRepositorySnapshot(
        snapshot,
        collectionKey,
        collectionId,
        deviceKeys,
      );
      await putJson(`/v1/collections/${collectionId}/head`, {
        expectedVersion: 0,
        nextHead: packed.headObjectId,
        signature: toBase64(
          sign(encoder.encode(packed.headObjectId), deviceKeys.signingPrivateKey),
        ),
      });
      const nextWorkspace: StoredWorkspace = {
        userId: user.id,
        repositoryId,
        collectionId,
        snapshot: { ...packed.snapshot, version: 1 },
        collectionKey: [...collectionKey],
        deviceKeys: serializeDeviceKeys(deviceKeys),
        recipientEmail: demoInfo?.recipientEmail,
        demoInstanceId: demoInfo?.instanceId,
        shareCollections: {},
        mode: 'owner',
        vaultMode: 'locked',
        label,
        icon,
        slug: options?.slug,
        deviceId,
      };
      if (seeded && demoInfo) {
        const recipientKeys = generateDeviceKeys();
        localStorage.setItem(
          'trove-demo-recipient-keys',
          JSON.stringify({
            instanceId: demoInfo.instanceId,
            keys: serializeDeviceKeys(recipientKeys),
          }),
        );
        await postJson('/v1/demo/bootstrap', {
          deviceId: uid('device'),
          encryptionPublicKey: toBase64(recipientKeys.encryptionPublicKey),
          signingPublicKey: toBase64(recipientKeys.signingPublicKey),
        });
      }
      await saveWorkspace(nextWorkspace);
      setWorkspace(nextWorkspace);
      setVaults(await listWorkspaces(user.id));
      if (!seeded) toastManager.add({ title: `${label} vault created` });
    } finally {
      setSyncing(false);
    }
  }

  async function createOpenVault(options: { label: string; icon?: string; slug?: string }) {
    if (!user) throw new Error('Sign in required');
    setSyncing(true);
    try {
      const deviceKeys = workspace?.deviceKeys
        ? deserializeDeviceKeys(workspace.deviceKeys)
        : generateDeviceKeys();
      const repositoryId = uid('repo');
      const collectionId = uid('collection');
      const deviceId = uid('device');
      const label = options.label.trim() || 'Shared';
      const icon = options.icon || DEFAULT_OPEN_VAULT_ICON;
      const emptyOid = '0'.repeat(40);
      const files: RepositoryFile[] = [
        {
          path: 'README.md',
          content: `# ${label}\n\nOpen collaboration space. Content is server-readable for merge and search.\n`,
          mimeType: 'text/markdown',
          size: 0,
          modifiedAt: new Date().toISOString(),
        },
        {
          path: 'AGENTS.md',
          content:
            '# Agents\n\nThis is an open Trove space. Prefer `trove` CLI for pull/push and resolve conflicts with Git-flavored sync.\n',
          mimeType: 'text/markdown',
          size: 0,
          modifiedAt: new Date().toISOString(),
        },
      ].map((file) => ({
        ...file,
        size: encoder.encode(file.content ?? '').byteLength,
      }));
      const firstCommit = await createCommit(
        files,
        null,
        `Create open space ${label}`,
        user.displayName,
      );
      const snapshot: RepositorySnapshot = {
        repositoryId,
        collectionId,
        files,
        commits: [firstCommit],
        head: firstCommit.id,
        version: 0,
      };
      await createRepositoryRequest({
        repositoryId,
        rootCollectionId: collectionId,
        deviceId,
        deviceEncryptionPublicKey: toBase64(deviceKeys.encryptionPublicKey),
        deviceSigningPublicKey: toBase64(deviceKeys.signingPublicKey),
        wrappedRootKey: '',
        head: emptyOid,
        mode: 'open',
        label,
        icon,
        slug: options.slug,
      });
      await commitGitSnapshot({
        repositoryId,
        files,
        message: `Create open space ${label}`,
        author: user.displayName,
        expectedObjectId: emptyOid,
      });
      const nextWorkspace: StoredWorkspace = {
        userId: user.id,
        repositoryId,
        collectionId,
        snapshot,
        collectionKey: [],
        deviceKeys: serializeDeviceKeys(deviceKeys),
        shareCollections: {},
        mode: 'owner',
        vaultMode: 'open',
        label,
        icon,
        slug: options.slug,
        deviceId,
      };
      await saveWorkspace(nextWorkspace);
      setWorkspace(nextWorkspace);
      setVaults(await listWorkspaces(user.id));
      toastManager.add({
        title: 'Shared space ready',
        description: `${label} is open for collaboration (server-readable).`,
      });
    } finally {
      setSyncing(false);
    }
  }

  async function switchVault(repositoryId: string) {
    if (!user) throw new Error('Sign in required');
    await setActiveRepositoryId(user.id, repositoryId);
    const next = await loadWorkspace(user.id, repositoryId);
    if (!next) throw new Error('Vault is not available on this device');
    setWorkspace(next);
    setVaults(await listWorkspaces(user.id));
  }

  async function restoreRepository(mnemonic: string, repositoryId?: string) {
    if (!user) throw new Error('Sign in required');
    setSyncing(true);
    try {
      const { repositories } = await getRepositories();
      const repository = repositoryId
        ? repositories.find((row) => row.id === repositoryId)
        : (repositories.find((row) => row.mode !== 'open') ?? repositories[0]);
      if (!repository) throw new Error('No repository is available for this account');
      if (repository.mode === 'open') {
        throw new Error(
          'Open spaces do not use recovery phrases — switch vaults from the sidebar.',
        );
      }
      const envelope = parseRootKeyEnvelope(repository.wrappedRootKey);
      if (!envelope.recovery) throw new Error('This repository has no recovery envelope');
      const collectionKey = await unwrapRootKeyFromRecovery(envelope.recovery, mnemonic);
      await installLockedRepository(repository, collectionKey);
      setVaults(await listWorkspaces(user.id));
      toastManager.add({
        title: 'Vault restored',
        description: 'Encrypted history is available on this device.',
      });
    } finally {
      setSyncing(false);
    }
  }

  async function unlockFromDevicePairing(
    deviceId: string,
    deviceKeys: DeviceKeys,
    repositoryId?: string,
  ) {
    if (!user) throw new Error('Sign in required');
    setSyncing(true);
    try {
      const { repositories } = await getRepositories();
      const repository = repositoryId
        ? repositories.find((row) => row.id === repositoryId)
        : (repositories.find((row) => row.mode !== 'open') ?? repositories[0]);
      if (!repository) throw new Error('No repository is available for this account');
      if (repository.mode === 'open') {
        const pulled = await pullOpenVault(repository.id);
        const files = pulled.files;
        const snapshot: RepositorySnapshot = {
          repositoryId: repository.id,
          collectionId: repository.rootCollectionId,
          files,
          commits: pulled.commits.map((commit, index) => ({
            id: `open_${index}_${commit.tree}`,
            treeId: commit.tree,
            parentId: commit.parent,
            message: commit.message,
            author: commit.author,
            authorEmail: '',
            committer: commit.author,
            committerEmail: '',
            createdAt: commit.createdAt,
            files,
          })),
          head: pulled.commitId ?? '0'.repeat(40),
          version: pulled.commits.length,
        };
        const nextWorkspace: StoredWorkspace = {
          userId: user.id,
          repositoryId: repository.id,
          collectionId: repository.rootCollectionId,
          snapshot,
          collectionKey: [],
          deviceKeys: serializeDeviceKeys(deviceKeys),
          shareCollections: {},
          mode: 'owner',
          vaultMode: 'open',
          label: repository.label || 'Shared',
          icon: repository.icon,
          slug: repository.slug,
          deviceId,
        };
        await saveWorkspace(nextWorkspace);
        setWorkspace(nextWorkspace);
        setVaults(await listWorkspaces(user.id));
        return;
      }
      const envelope = parseRootKeyEnvelope(repository.wrappedRootKey);
      const deviceWrap = envelope.devices?.[deviceId];
      if (!deviceWrap) {
        throw new Error(
          'No vault key was delivered. Approve again from a browser that already has this trove unlocked.',
        );
      }
      const collectionKey = await unwrapKey(deviceWrap, deviceKeys.encryptionPrivateKey);
      const object = await readHeadObject(repository.head);
      if (object.kind !== 'manifest') {
        throw new Error(
          'Repository head is not a manifest yet. Open the vault in the browser on a device that still has a local copy so it can republish.',
        );
      }
      const remoteSnapshot = await loadSnapshotFromHeadObject(object, collectionKey, {
        hydrateHead: true,
      });
      const needsAgents = !hasAgentsGuide(remoteSnapshot.commits.at(-1)?.files ?? []);
      let snapshot = ensureAgentsGuide(remoteSnapshot);
      const nextWorkspace: StoredWorkspace = {
        userId: user.id,
        repositoryId: repository.id,
        collectionId: repository.rootCollectionId,
        snapshot,
        collectionKey: [...collectionKey],
        deviceKeys: serializeDeviceKeys(deviceKeys),
        shareCollections: {},
        mode: 'owner',
        vaultMode: 'locked',
        label: repository.label || 'Personal',
        icon: repository.icon,
        slug: repository.slug,
        deviceId,
      };
      if (needsAgents) {
        snapshot = await commitWorkingSnapshot(snapshot, 'Add AGENTS.md', user.displayName);
        await syncCommitted(nextWorkspace, snapshot);
      } else {
        await saveWorkspace(nextWorkspace);
        setWorkspace(nextWorkspace);
      }
      setVaults(await listWorkspaces(user.id));
      toastManager.add({
        title: 'Device unlocked',
        description: 'This browser can open your files.',
      });
    } finally {
      setSyncing(false);
    }
  }

  async function syncCommitted(nextWorkspace: StoredWorkspace, snapshot: RepositorySnapshot) {
    if (nextWorkspace.vaultMode === 'open') {
      const files = snapshot.files.map((file) => ({
        ...file,
        content: file.content ?? '',
      }));
      const { commitId } = await commitGitSnapshot({
        repositoryId: nextWorkspace.repositoryId,
        files,
        message: snapshot.commits.at(-1)?.message ?? 'Update',
        author: author,
        expectedObjectId: nextWorkspace.snapshot.head || null,
      });
      const saved = {
        ...nextWorkspace,
        snapshot: {
          ...snapshot,
          files,
          head: commitId,
          version: nextWorkspace.snapshot.version + 1,
        },
        pendingBaseline: undefined,
      };
      await saveWorkspace(saved);
      setWorkspace(saved);
      return;
    }
    const files = await ensureSnapshotFilesContent(
      snapshot.files,
      Uint8Array.from(nextWorkspace.collectionKey),
    );
    const keys = deserializeDeviceKeys(nextWorkspace.deviceKeys);
    const packed = await uploadRepositorySnapshot(
      { ...snapshot, files },
      Uint8Array.from(nextWorkspace.collectionKey),
      nextWorkspace.collectionId,
      keys,
    );
    await putJson(`/v1/collections/${nextWorkspace.collectionId}/head`, {
      expectedVersion: (nextWorkspace.pendingBaseline ?? nextWorkspace.snapshot).version,
      nextHead: packed.headObjectId,
      signature: toBase64(sign(encoder.encode(packed.headObjectId), keys.signingPrivateKey)),
    });
    const saved = {
      ...nextWorkspace,
      snapshot: packed.snapshot,
      pendingBaseline: undefined,
    };
    await saveWorkspace(saved);
    setWorkspace(saved);
    void queryClient.invalidateQueries({ queryKey: ['storage'] });
  }

  async function persistMutation(draft: RepositorySnapshot, message: string) {
    if (!workspace || workspace.mode !== 'owner') throw new Error('This collection is read-only');
    if (saveMode === 'queued') {
      const next = {
        ...workspace,
        snapshot: draft,
        pendingBaseline: workspace.pendingBaseline ?? workspace.snapshot,
      };
      await saveWorkspace(next);
      setWorkspace(next);
      return;
    }
    setSyncing(true);
    try {
      const committed = await commitWorkingSnapshot(draft, message, author);
      await syncCommitted(workspace, committed);
      toastManager.add({
        title: 'Snapshot saved',
        description: 'A signed, encrypted commit is now current.',
      });
    } catch (cause) {
      if (cause instanceof ApiClientError && cause.code === 'quota_exceeded') {
        toastManager.add({ title: 'Storage full', description: cause.message });
      }
      throw cause;
    } finally {
      setSyncing(false);
    }
  }

  async function saveFile(
    path: string,
    content: string,
    mimeType = 'text/markdown',
    annotation?: EntryAnnotation | null,
  ) {
    if (!workspace || workspace.mode !== 'owner') throw new Error('This collection is read-only');
    if (isHiddenMetaPath(path)) throw new Error('That path is reserved for repository metadata');
    let draft = draftUpsertFile(workspace.snapshot, path, content, mimeType);
    if (annotation !== undefined) {
      draft = draftSetAnnotation(draft, path, annotation);
    }
    const exists = workspace.snapshot.files.some((file) => file.path === path);
    await persistMutation(draft, exists ? `Update ${path}` : `Add ${path}`);
  }

  async function saveFiles(files: Array<{ path: string; content: string; mimeType: string }>) {
    if (!workspace || workspace.mode !== 'owner') throw new Error('This collection is read-only');
    if (!files.length) return;
    if (files.some((file) => isHiddenMetaPath(file.path))) {
      throw new Error('That path is reserved for repository metadata');
    }
    const draft = draftUpsertFiles(workspace.snapshot, files);
    await persistMutation(draft, `Upload ${files.length} ${files.length === 1 ? 'file' : 'files'}`);
  }

  async function createFolder(path: string, annotation?: EntryAnnotation | null) {
    if (!workspace || workspace.mode !== 'owner') throw new Error('This collection is read-only');
    const folder = path.replace(/^\/+|\/+$/g, '');
    if (!folder || folder.includes('//') || isHiddenMetaPath(folder)) {
      throw new Error('That folder path is reserved');
    }
    const keepPath = keepPathForFolder(folder);
    if (
      workspace.snapshot.files.some(
        (file) => file.path === keepPath || file.path.startsWith(`${folder}/`),
      )
    ) {
      throw new Error('That folder already exists');
    }
    let draft = draftUpsertFile(workspace.snapshot, keepPath, '', 'text/plain');
    if (annotation) {
      draft = draftSetAnnotation(draft, folder, annotation);
    }
    await persistMutation(draft, `Add ${folder}/`);
  }

  async function moveEntry(sourcePath: string, targetFolder: string) {
    if (!workspace || workspace.mode !== 'owner') throw new Error('This collection is read-only');
    const draft = draftMovePath(workspace.snapshot, sourcePath, targetFolder);
    await persistMutation(draft, `Move ${sourcePath} to ${targetFolder || 'repository root'}`);
  }

  async function renameEntry(sourcePath: string, nextName: string) {
    if (!workspace || workspace.mode !== 'owner') throw new Error('This collection is read-only');
    const draft = draftRenamePath(workspace.snapshot, sourcePath, nextName);
    const parent = sourcePath.includes('/') ? sourcePath.slice(0, sourcePath.lastIndexOf('/')) : '';
    const destination = parent ? `${parent}/${nextName.trim()}` : nextName.trim();
    await persistMutation(draft, `Rename ${sourcePath} to ${destination}`);
    return destination;
  }

  async function deleteEntry(sourcePath: string) {
    if (!workspace || workspace.mode !== 'owner') throw new Error('This collection is read-only');
    const draft = draftRemovePath(workspace.snapshot, sourcePath);
    await persistMutation(draft, `Delete ${sourcePath}`);
  }

  async function setEntryAnnotation(path: string, annotation: EntryAnnotation | null) {
    if (!workspace || workspace.mode !== 'owner') throw new Error('This collection is read-only');
    const draft = draftSetAnnotation(workspace.snapshot, path, annotation);
    await persistMutation(draft, annotation ? `Annotate ${path}` : `Clear annotations on ${path}`);
  }

  async function updateFolder(
    path: string,
    next: { name?: string; annotation?: EntryAnnotation | null },
  ) {
    if (!workspace || workspace.mode !== 'owner') throw new Error('This collection is read-only');
    let draft = workspace.snapshot;
    let currentPath = path;
    const currentName = path.split('/').at(-1) ?? path;
    const nextName = next.name?.trim();
    const renaming = Boolean(nextName && nextName !== currentName);

    if (renaming && nextName) {
      draft = draftRenamePath(draft, currentPath, nextName);
      const parent = currentPath.includes('/')
        ? currentPath.slice(0, currentPath.lastIndexOf('/'))
        : '';
      currentPath = parent ? `${parent}/${nextName}` : nextName;
    }

    if (next.annotation !== undefined) {
      draft = draftSetAnnotation(draft, currentPath, next.annotation);
    }

    const message = renaming
      ? next.annotation !== undefined
        ? `Update ${currentPath}`
        : `Rename ${path} to ${currentPath}`
      : next.annotation
        ? `Annotate ${currentPath}`
        : `Clear annotations on ${currentPath}`;
    await persistMutation(draft, message);
    return currentPath;
  }

  async function commitPending(message = 'Save pending changes') {
    if (!workspace || workspace.mode !== 'owner' || !workspace.pendingBaseline) return;
    setSyncing(true);
    try {
      const committed = await commitWorkingSnapshot(workspace.snapshot, message, author);
      await syncCommitted(workspace, committed);
      toastManager.add({
        title: 'Changes saved',
        description: 'Queued edits were committed as one signed snapshot.',
      });
    } catch (cause) {
      if (cause instanceof ApiClientError && cause.code === 'quota_exceeded') {
        toastManager.add({ title: 'Storage full', description: cause.message });
      }
      throw cause;
    } finally {
      setSyncing(false);
    }
  }

  async function discardPending() {
    if (!workspace?.pendingBaseline) return;
    const next = {
      ...workspace,
      snapshot: workspace.pendingBaseline,
      pendingBaseline: undefined,
    };
    await saveWorkspace(next);
    setWorkspace(next);
    toastManager.add({ title: 'Discarded local changes' });
  }

  async function shareFolder(
    folder: string,
    recipientEmail: string,
    options: {
      expiresAt: string | null;
      maxViews?: 1 | null;
      accessCode?: string | null;
    },
  ) {
    if (!workspace || workspace.mode !== 'owner')
      throw new Error('Only repository owners can share folders');
    if (workspace.pendingBaseline) {
      throw new Error('Save or discard queued changes before sharing');
    }
    setSyncing(true);
    try {
      const directory = await api<{ encryptionPublicKey: string }>(
        `/v1/directory?email=${encodeURIComponent(recipientEmail)}`,
      );
      const sourceFiles = await ensureSnapshotFilesContent(
        filesWithin(workspace.snapshot.files, folder),
        Uint8Array.from(workspace.collectionKey),
      );
      if (!sourceFiles.length) throw new Error('Folder is empty or does not exist');
      const folderPrefix = `${folder}/`;
      const files = sourceFiles.map((file) => ({
        ...file,
        path: file.path.startsWith(folderPrefix) ? file.path.slice(folderPrefix.length) : file.path,
        blobOpaqueId: undefined,
        contentHash: undefined,
      }));
      const sharedAnnotations = Object.fromEntries(
        Object.entries(annotationsOf(workspace.snapshot) ?? {})
          .filter(([path]) => path === folder || path.startsWith(folderPrefix))
          .map(([path, value]) => [path === folder ? '.' : path.slice(folderPrefix.length), value])
          .filter(([path]) => path !== '.'),
      ) as EntryAnnotations;
      const collectionId = uid('collection');
      const collectionKey = randomBytes(32);
      const sharedFiles = withAnnotationsFile(
        files,
        Object.keys(sharedAnnotations).length ? sharedAnnotations : undefined,
      );
      const commit = await createCommit(
        sharedFiles,
        null,
        `Share ${folder}`,
        user?.displayName ?? 'Trove user',
      );
      const snapshot: RepositorySnapshot & { collectionLabel: string } = {
        repositoryId: workspace.repositoryId,
        collectionId,
        files: sharedFiles,
        commits: [commit],
        head: commit.id,
        version: 0,
        collectionLabel: folder.split('/').at(-1) ?? 'Shared folder',
      };
      const keys = deserializeDeviceKeys(workspace.deviceKeys);
      const packed = await uploadRepositorySnapshot(snapshot, collectionKey, collectionId, keys);
      const deviceResponse = await api<{ devices: Array<{ id: string }> }>('/v1/devices');
      await postJson('/v1/collections', {
        collectionId,
        repositoryId: workspace.repositoryId,
        head: packed.headObjectId,
        deviceId: deviceResponse.devices[0]?.id,
      });
      const wrapped = serializeWrappedKey(
        await wrapKey(collectionKey, fromBase64(directory.encryptionPublicKey)),
      );
      const response = await postJson<{ shareId: string }>('/v1/shares', {
        collectionId,
        recipientEmail,
        rights: ['read'],
        expiresAt: options.expiresAt,
        maxViews: options.maxViews ?? null,
        accessCode: options.accessCode ? options.accessCode.toUpperCase() : null,
        wrappedKey: wrapped,
        ownerSignature: toBase64(sign(encoder.encode(wrapped), keys.signingPrivateKey)),
        collectionLabel: `encrypted:${collectionId}`,
      });
      const next = {
        ...workspace,
        shareCollections: {
          ...workspace.shareCollections,
          [folder]: {
            collectionId,
            key: [...collectionKey],
            head: packed.headObjectId,
            version: 0,
            shareId: response.shareId,
          },
        },
      };
      await saveWorkspace(next);
      setWorkspace(next);
      toastManager.add({
        title: `${folder} shared`,
        ...(options.accessCode ? { description: `Magic code ${options.accessCode}` } : {}),
      });
    } finally {
      setSyncing(false);
    }
  }

  async function acceptIncomingShare(share: ShareRecord, accessCode?: string) {
    if (!user) throw new Error('Sign in required');
    setSyncing(true);
    try {
      const saved = localStorage.getItem('trove-demo-recipient-keys');
      if (!saved) throw new Error('This demo recipient key is unavailable in this browser');
      const parsed = JSON.parse(saved) as { keys: StoredWorkspace['deviceKeys'] };
      const keys = deserializeDeviceKeys(parsed.keys);
      if (share.status === 'pending') {
        const devices = await api<{ devices: Array<{ id: string }> }>('/v1/devices');
        await postJson(`/v1/shares/${share.id}/accept`, {
          recipientDeviceId: devices.devices[0]?.id,
          ...(accessCode ? { accessCode } : {}),
        });
      }
      const collectionKey = await unwrapKey(
        JSON.parse(decoder.decode(fromBase64(share.wrappedKey))) as Parameters<typeof unwrapKey>[0],
        keys.encryptionPrivateKey,
      );
      const head = await api<{ opaqueHeadId: string; version: number }>(
        `/v1/collections/${share.collectionId}/head`,
      );
      const object = await readHeadObject(head.opaqueHeadId);
      const loaded = await loadSnapshotFromHeadObject(object, collectionKey, {
        hydrateHead: true,
      });
      const snapshot = loaded as RepositorySnapshot & { collectionLabel?: string };
      const next: StoredWorkspace = {
        userId: user.id,
        repositoryId: snapshot.repositoryId,
        collectionId: share.collectionId,
        snapshot: { ...snapshot, version: head.version },
        collectionKey: [...collectionKey],
        deviceKeys: parsed.keys,
        recipientEmail: user.email,
        shareCollections: {},
        mode: 'shared',
        sharedLabel: snapshot.collectionLabel ?? 'Shared folder',
        vaultMode: 'locked',
        label: snapshot.collectionLabel ?? 'Shared folder',
        icon: 'folder',
      };
      await saveWorkspace(next);
      setWorkspace(next);
      toastManager.add({
        title: 'Invitation accepted',
        description: 'The shared collection was decrypted locally.',
      });
    } finally {
      setSyncing(false);
    }
  }

  async function revokeShare(shareId: string) {
    if (!workspace) return;
    setSyncing(true);
    try {
      await deleteJson(`/v1/shares/${shareId}`);
      const entries = Object.entries(workspace.shareCollections);
      const target = entries.find(([, value]) => value.shareId === shareId);
      if (target) {
        const [folder, value] = target;
        const next = {
          ...workspace,
          shareCollections: {
            ...workspace.shareCollections,
            [folder]: { ...value, key: [...randomBytes(32)] },
          },
        };
        await saveWorkspace(next);
        setWorkspace(next);
      }
      toastManager.add({
        title: 'Access revoked',
        description:
          'A new key protects future versions. Previously downloaded copies remain available.',
      });
    } finally {
      setSyncing(false);
    }
  }

  const value = useMemo<WorkspaceContextValue>(
    () => ({
      workspace,
      vaults,
      loading,
      syncing,
      demoSetupError,
      dirty,
      saveMode,
      setSaveMode,
      createRepository,
      createOpenVault,
      switchVault,
      restoreRepository,
      unlockFromDevicePairing,
      saveFile,
      saveFiles,
      createFolder,
      moveEntry,
      renameEntry,
      deleteEntry,
      setEntryAnnotation,
      updateFolder,
      commitPending,
      discardPending,
      shareFolder,
      acceptIncomingShare,
      revokeShare,
      refresh,
    }),
    [workspace, vaults, loading, syncing, demoSetupError, dirty, saveMode, user?.id],
  );

  return <WorkspaceContext.Provider value={value}>{children}</WorkspaceContext.Provider>;
}

export function useWorkspace() {
  const context = useContext(WorkspaceContext);
  if (!context) throw new Error('useWorkspace must be used within WorkspaceProvider');
  return context;
}
